import { AddIcon } from '@chakra-ui/icons';
import {
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    FormControl,
    FormLabel,
    HStack,
    Image,
    Input,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    SimpleGrid,
    Switch,
    Text,
    Textarea,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { Currency } from '../../../models/court-pricing/dto/court-pricing-rule.dto';
import { SportType } from '../../../models/court/dto/court.dto';
import { CreateProductDto, ProductCategory } from '../../../models/product/dto/product.dto';
import { useAvailableLocations } from '../../location-management/hooks/useAvailableLocations';
import { useIsSingleClub } from '../../location-management/hooks/useSingleClubAutoSelect';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { parsePrice } from '../../../utils/parse-price';
import { CreateProductWithImageInput, useCreateProduct } from '../hooks/useCreateProduct';

export default function CreateProductForm() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { selectedLocation } = useSelectedLocation();
    const { data: availableLocations } = useAvailableLocations();
    const createProductMutation = useCreateProduct();
    const toast = useToast();
    const imageBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
    const subtleTextColor = useColorModeValue('gray.600', 'gray.400');

    const [formData, setFormData] = useState<Omit<CreateProductDto, 'imageUrl'>>({
        name: '',
        description: '',
        sports: [],
        price: 0,
        currency: Currency.EUR,
        stockQuantity: 0,
        category: ProductCategory.EQUIPMENT,
        locationId: selectedLocation?.id || availableLocations?.[0]?.id || '',
    });

    const [isUnlimitedStock, setIsUnlimitedStock] = useState(false);

    // MYS-66: hide the club selector when the org has a single club, and make
    // sure locationId is filled from the sole club (the initial state can miss
    // it if the locations query resolves after mount).
    const { isSingleClub, soleLocation } = useIsSingleClub();
    useEffect(() => {
        if (soleLocation && !formData.locationId) {
            setFormData((prev) => ({ ...prev, locationId: soleLocation.id }));
        }
    }, [soleLocation, formData.locationId]);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = async () => {
        // Validation: Ensure a location is selected
        if (!formData.locationId) {
            toast({
                title: translate('product.location_required') || 'Location is required',
                description: translate('product.select_location_message') || 'Please select a location to create the product',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        const submitData: CreateProductWithImageInput = {
            productData: {
                ...formData,
                hasUnlimitedStock: isUnlimitedStock,
            },
            imageFile: selectedImage || undefined,
        };

        try {
            await createProductMutation.mutateAsync(submitData);
            toast({
                title: translate('product.create_success') || 'Product created successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose();
            resetForm();
        } catch (error) {
            toast({
                title: translate('product.create_error') || 'Error creating product',
                description: error instanceof Error ? error.message : (translate('product.unknown_error') || 'Unknown error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            sports: [],
            price: 0,
            currency: Currency.EUR,
            stockQuantity: 0,
            category: ProductCategory.EQUIPMENT,
            locationId: selectedLocation?.id || availableLocations?.[0]?.id || '',
        });
        setIsUnlimitedStock(false);
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSportsChange = (selectedSports: string[]) => {
        setFormData(prev => ({
            ...prev,
            sports: selectedSports as SportType[]
        }));
    };

    return (
        <>
            <Button
                onClick={onOpen}
                colorScheme="brand"
                leftIcon={<AddIcon />}
                size="sm"
            >
                {translate('product.create') || 'Create Product'}
            </Button>

            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('product.create') || 'Create Product'}
                onSubmit={handleSubmit}
                submitLabel={translate('product.create') || 'Create Product'}
                cancelLabel={translate('product.cancel') || 'Cancel'}
                isSubmitting={createProductMutation.isLoading}
                size="2xl"
            >
                            <FormStack density="section">
                                <FormControl isRequired>
                                    <FormLabel>{translate('product.name') || 'Name'}</FormLabel>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder={translate('product.name_placeholder') || 'Enter product name'}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>{translate('product.description') || 'Description'}</FormLabel>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder={translate('product.description_placeholder') || 'Enter product description'}
                                        rows={3}
                                    />
                                </FormControl>

                                <SimpleGrid columns={2} spacing={4} width="100%">
                                    <FormControl isRequired>
                                        <FormLabel>{translate('product.category') || 'Category'}</FormLabel>
                                        <Select
                                            value={formData.category}
                                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as ProductCategory }))}
                                        >
                                            {Object.values(ProductCategory).map((category) => (
                                                <option key={category} value={category}>
                                                    {translate(`product.categories.${category}` as any) || category}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {!isSingleClub && (
                                        <FormControl isRequired>
                                            <FormLabel>{translate('product.location') || 'Location'}</FormLabel>
                                            <Select
                                                value={formData.locationId}
                                                onChange={(e) => setFormData(prev => ({ ...prev, locationId: e.target.value }))}
                                            >
                                                {availableLocations?.map((location) => (
                                                    <option key={location.id} value={location.id}>
                                                        {location.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </SimpleGrid>

                                <SimpleGrid columns={2} spacing={4} width="100%">
                                    <FormControl isRequired>
                                        <FormLabel>{translate('product.price') || 'Price'}</FormLabel>
                                        <NumberInput
                                            value={formData.price}
                                            onChange={(valueString) =>
                                                setFormData(prev => ({ ...prev, price: parsePrice(valueString) || 0 }))
                                            }
                                            min={0}
                                            precision={2}
                                        >
                                            <NumberInputField />
                                            <NumberInputStepper>
                                                <NumberIncrementStepper />
                                                <NumberDecrementStepper />
                                            </NumberInputStepper>
                                        </NumberInput>
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel>{translate('product.currency') || 'Currency'}</FormLabel>
                                        <Select
                                            value={formData.currency}
                                            onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value as Currency }))}
                                        >
                                            {Object.values(Currency).map((currency) => (
                                                <option key={currency} value={currency}>
                                                    {currency.toUpperCase()}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </SimpleGrid>

                                <FormControl>
                                    <FormLabel>{translate('product.stock') || 'Stock'}</FormLabel>
                                    <VStack align="start" spacing={2}>
                                        <HStack>
                                            <Switch
                                                isChecked={isUnlimitedStock}
                                                onChange={(e) => setIsUnlimitedStock(e.target.checked)}
                                            />
                                            <Text fontSize="sm">{translate('product.unlimited_stock') || 'Unlimited stock'}</Text>
                                        </HStack>
                                        {!isUnlimitedStock && (
                                            <NumberInput
                                                value={formData.stockQuantity}
                                                onChange={(valueString, valueNumber) =>
                                                    setFormData(prev => ({ ...prev, stockQuantity: valueNumber || 0 }))
                                                }
                                                min={0}
                                                width="100%"
                                            >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                </NumberInputStepper>
                                            </NumberInput>
                                        )}
                                    </VStack>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>{translate('product.associated_sports') || 'Associated Sports'}</FormLabel>
                                    <CheckboxGroup
                                        value={formData.sports}
                                        onChange={handleSportsChange}
                                    >
                                        <SimpleGrid columns={2} spacing={2}>
                                            {Object.values(SportType).map((sport) => (
                                                <Checkbox key={sport} value={sport}>
                                                    {sport}
                                                </Checkbox>
                                            ))}
                                        </SimpleGrid>
                                    </CheckboxGroup>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>{translate('product.image') || 'Product Image'}</FormLabel>
                                    <VStack align="start" spacing={3}>
                                        <Input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            display="none"
                                        />
                                        <HStack>
                                            <Button
                                                onClick={() => fileInputRef.current?.click()}
                                                variant="outline"
                                                size="sm"
                                            >
                                                {translate('product.select_image') || 'Select Image'}
                                            </Button>
                                            {selectedImage && (
                                                <Button
                                                    onClick={handleRemoveImage}
                                                    variant="ghost"
                                                    size="sm"
                                                    colorScheme="red"
                                                >
                                                    {translate('product.remove') || 'Remove'}
                                                </Button>
                                            )}
                                        </HStack>
                                        {imagePreview && (
                                            <Box>
                                                <Text fontSize="sm" mb={2}>
                                                    {translate('product.preview') || 'Preview:'}
                                                </Text>
                                                <Image
                                                    src={imagePreview}
                                                    alt="Product preview"
                                                    maxW="200px"
                                                    maxH="200px"
                                                    objectFit="cover"
                                                    borderRadius="md"
                                                    border="1px solid"
                                                    borderColor={imageBorderColor}
                                                />
                                            </Box>
                                        )}
                                        {selectedImage && (
                                            <Text fontSize="sm" color={subtleTextColor}>
                                                {translate('product.selected') || 'Selected:'} {selectedImage.name}
                                            </Text>
                                        )}
                                    </VStack>
                                </FormControl>
                            </FormStack>
            </FormModal>
        </>
    );
}
